import { c as _c } from "react/compiler-runtime";
// Thrid Party
import * as React from 'react';

// Utils
import getConfig from '@config';
import { useLazyQuery } from '@apollo/client/react';
import { SubscriptionType, PlansListAllDocument } from '@gql';
type GqlPlan = {
  id: string;
  name: string;
  amount: number;
};
export type Plan = {
  id: string;
  name: string;
  amount: number;
  subscriptionType: SubscriptionType;
};

/**
 * @description This hook is used to get the price plans from the ENV.
 * It supports both lazy loading the plans from the API,
 * and receiving them as an argument for better performance.
 */
export function usePlans(plans: GqlPlan[]): {
  freePlan: Plan;
  bronzePlan: Plan;
  silverPlan: Plan;
  goldPlan: Plan;
  getPlanBySubscriptionType: (type: SubscriptionType) => Plan;
};
export function usePlans(): {
  freePlan: Plan | undefined;
  bronzePlan: Plan | undefined;
  silverPlan: Plan | undefined;
  goldPlan: Plan | undefined;
  getPlanBySubscriptionType: (type: SubscriptionType) => Plan | undefined;
};
export function usePlans(t0) {
  const $ = _c(52);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const plans = t1;
  const [plansListAllLazyQuery] = useLazyQuery(PlansListAllDocument);
  const [freePlan, setFreePlan] = React.useState(undefined);
  const [bronzePlan, setBronzePlan] = React.useState(undefined);
  const [silverPlan, setSilverPlan] = React.useState(undefined);
  const [goldPlan, setGoldPlan] = React.useState(undefined);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getConfig();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    publicRuntimeConfig: t3
  } = t2;
  const {
    stripe: t4
  } = t3;
  const {
    subscriptionProducts: t5
  } = t4;
  const {
    freeMonthly,
    bronzeMonthly,
    silverMonthly,
    goldMonthly
  } = t5;
  let t6;
  if ($[3] !== plans || $[4] !== plansListAllLazyQuery) {
    t6 = () => {
      const setPlans = async function setPlans() {
        if (!plans) {
          const {
            data
          } = await plansListAllLazyQuery();
          const plans_0 = data?.plansListAll.__typename === "PlanCollection" ? data.plansListAll.entries : [];
          if (data) {
            const _freePlan = plans_0.find(plan => plan.id === freeMonthly.id);
            if (_freePlan) {
              setFreePlan({
                ..._freePlan,
                subscriptionType: SubscriptionType.Free
              });
            }
            const _bronzePlan = plans_0.find(plan_0 => plan_0.id === bronzeMonthly.id);
            if (_bronzePlan) {
              setBronzePlan({
                ..._bronzePlan,
                subscriptionType: SubscriptionType.Bronze
              });
            }
            const _silverPlan = plans_0.find(plan_1 => plan_1.id === silverMonthly.id);
            if (_silverPlan) {
              setSilverPlan({
                ..._silverPlan,
                subscriptionType: SubscriptionType.Silver
              });
            }
            const _goldPlan = plans_0.find(plan_2 => plan_2.id === goldMonthly.id);
            if (_goldPlan) {
              setGoldPlan({
                ..._goldPlan,
                subscriptionType: SubscriptionType.Gold
              });
            }
          }
        }
      };
      setPlans();
    };
    $[3] = plans;
    $[4] = plansListAllLazyQuery;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== plans) {
    t7 = JSON.stringify(plans);
    $[6] = plans;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t7) {
    t8 = [t7];
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  React.useEffect(t6, t8);
  if (plans) {
    let t9;
    if ($[10] !== plans) {
      let t10;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = plan_3 => plan_3.id === freeMonthly.id;
        $[12] = t10;
      } else {
        t10 = $[12];
      }
      t9 = plans.find(t10);
      $[10] = plans;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    const _freePlan_0 = t9;
    let t10;
    if ($[13] !== plans) {
      let t11;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = plan_4 => plan_4.id === bronzeMonthly.id;
        $[15] = t11;
      } else {
        t11 = $[15];
      }
      t10 = plans.find(t11);
      $[13] = plans;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    const _bronzePlan_0 = t10;
    let t11;
    if ($[16] !== plans) {
      let t12;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = plan_5 => plan_5.id === silverMonthly.id;
        $[18] = t12;
      } else {
        t12 = $[18];
      }
      t11 = plans.find(t12);
      $[16] = plans;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    const _silverPlan_0 = t11;
    let t12;
    if ($[19] !== plans) {
      let t13;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = plan_6 => plan_6.id === goldMonthly.id;
        $[21] = t13;
      } else {
        t13 = $[21];
      }
      t12 = plans.find(t13);
      $[19] = plans;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    const _goldPlan_0 = t12;
    if (!_freePlan_0) {
      throw Error("Free plan is not set in the ENV.");
    }
    if (!_bronzePlan_0) {
      throw Error("Boost plan is not set in the ENV.");
    }
    if (!_silverPlan_0) {
      throw Error("Boost Plus plan is not set in the ENV.");
    }
    if (!_goldPlan_0) {
      throw Error("Boost Max plan is not set in the ENV.");
    }
    let t13;
    if ($[22] !== _bronzePlan_0 || $[23] !== _freePlan_0 || $[24] !== _goldPlan_0 || $[25] !== _silverPlan_0) {
      t13 = type => {
        switch (type) {
          case SubscriptionType.Free:
            {
              return {
                ..._freePlan_0,
                subscriptionType: SubscriptionType.Free
              };
            }
          case SubscriptionType.Bronze:
            {
              return {
                ..._bronzePlan_0,
                subscriptionType: SubscriptionType.Bronze
              };
            }
          case SubscriptionType.Silver:
            {
              return {
                ..._silverPlan_0,
                subscriptionType: SubscriptionType.Silver
              };
            }
          case SubscriptionType.Gold:
            {
              return {
                ..._goldPlan_0,
                subscriptionType: SubscriptionType.Gold
              };
            }
          default:
            {
              throw Error(`Plan is not set in the ENV for subscription type: ${type}.`);
            }
        }
      };
      $[22] = _bronzePlan_0;
      $[23] = _freePlan_0;
      $[24] = _goldPlan_0;
      $[25] = _silverPlan_0;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    const getPlanBySubscriptionType = t13;
    let t14;
    if ($[27] !== _freePlan_0) {
      t14 = {
        ..._freePlan_0,
        subscriptionType: SubscriptionType.Free
      };
      $[27] = _freePlan_0;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    let t15;
    if ($[29] !== _bronzePlan_0) {
      t15 = {
        ..._bronzePlan_0,
        subscriptionType: SubscriptionType.Bronze
      };
      $[29] = _bronzePlan_0;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    let t16;
    if ($[31] !== _silverPlan_0) {
      t16 = {
        ..._silverPlan_0,
        subscriptionType: SubscriptionType.Silver
      };
      $[31] = _silverPlan_0;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    let t17;
    if ($[33] !== _goldPlan_0) {
      t17 = {
        ..._goldPlan_0,
        subscriptionType: SubscriptionType.Gold
      };
      $[33] = _goldPlan_0;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    let t18;
    if ($[35] !== getPlanBySubscriptionType || $[36] !== t14 || $[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
      t18 = {
        freePlan: t14,
        bronzePlan: t15,
        silverPlan: t16,
        goldPlan: t17,
        getPlanBySubscriptionType
      };
      $[35] = getPlanBySubscriptionType;
      $[36] = t14;
      $[37] = t15;
      $[38] = t16;
      $[39] = t17;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    return t18;
  } else {
    let t9;
    if ($[41] !== bronzePlan || $[42] !== freePlan || $[43] !== goldPlan || $[44] !== silverPlan) {
      t9 = type_0 => {
        switch (type_0) {
          case SubscriptionType.Free:
            {
              return freePlan;
            }
          case SubscriptionType.Bronze:
            {
              return bronzePlan;
            }
          case SubscriptionType.Silver:
            {
              return silverPlan;
            }
          case SubscriptionType.Gold:
            {
              return goldPlan;
            }
          default:
            {
              return;
            }
        }
      };
      $[41] = bronzePlan;
      $[42] = freePlan;
      $[43] = goldPlan;
      $[44] = silverPlan;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    const getPlanBySubscriptionType_0 = t9;
    let t10;
    if ($[46] !== bronzePlan || $[47] !== freePlan || $[48] !== getPlanBySubscriptionType_0 || $[49] !== goldPlan || $[50] !== silverPlan) {
      t10 = {
        freePlan,
        bronzePlan,
        silverPlan,
        goldPlan,
        getPlanBySubscriptionType: getPlanBySubscriptionType_0
      };
      $[46] = bronzePlan;
      $[47] = freePlan;
      $[48] = getPlanBySubscriptionType_0;
      $[49] = goldPlan;
      $[50] = silverPlan;
      $[51] = t10;
    } else {
      t10 = $[51];
    }
    return t10;
  }
}